import React, { useEffect, useState } from 'react';
import { Calendar } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import {
  criarAgendamento,
  AgendamentoCreate,
  AgendamentoResponse,
  parseApiError,
} from '../../services/api.ts';
import BaseModal from './_BaseModal.tsx';
import { getAuth, toBRDateTimeString, SOURCE_OPTIONS } from './_helpers.ts';

interface CreateAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: number | null | undefined;
  contactName: string;
  contactPhone: string;
  contactSourceId?: string;
  clinicId: number;
  onCreated?: (agendamento: AgendamentoResponse) => void;
}

const CreateAppointmentModal: React.FC<CreateAppointmentModalProps> = ({
  isOpen,
  onClose,
  leadId,
  contactName,
  contactPhone,
  contactSourceId,
  clinicId,
  onCreated,
}) => {
  const { isDark } = useTheme();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    date: '',
    time: '',
    midia: contactSourceId || 'Facebook',
    interesse: '',
  });

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setForm({
        date: '',
        time: '',
        midia: contactSourceId || 'Facebook',
        interesse: '',
      });
    }
  }, [isOpen, contactSourceId]);

  const isMissingLead = !leadId;
  const canSubmit = !!leadId && !!form.date && !!form.time;

  const handleSubmit = async () => {
    if (!leadId || !canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const auth = getAuth(clinicId);
      const payload: AgendamentoCreate = {
        lead_id: leadId,
        nome: contactName,
        phone: contactPhone,
        consulta_data: toBRDateTimeString(form.date, form.time),
        midia: form.midia,
        interesse: form.interesse,
      };
      const novo = await criarAgendamento(auth.effectiveClientId, auth.clinicId, payload);
      onCreated?.(novo);
      onClose();
    } catch (err: any) {
      const parsed = parseApiError(err, 'Não foi possível criar o agendamento.');
      const alternatives = parsed.alternatives?.length
        ? ` Horários alternativos: ${parsed.alternatives.join(' ou ')}.`
        : '';
      setError(`${parsed.message}${alternatives}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Agendar consulta"
      subtitle="Crie um agendamento para este lead"
      icon={Calendar}
      primaryLabel="Agendar"
      onPrimary={isMissingLead ? undefined : handleSubmit}
      primaryDisabled={!canSubmit}
      saving={saving}
      error={error}
    >
      {isMissingLead ? (
        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          Este contato ainda não é um lead. Cadastre-o como lead pelo CRM antes de agendar.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Data
              </label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className={`w-full p-3 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>
            <div className="space-y-2">
              <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Hora
              </label>
              <input
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                className={`w-full p-3 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Mídia
            </label>
            <select
              value={form.midia}
              onChange={(e) => setForm({ ...form, midia: e.target.value })}
              className={`w-full p-3 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              {SOURCE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
              {form.midia && !SOURCE_OPTIONS.includes(form.midia) && (
                <option value={form.midia}>{form.midia}</option>
              )}
            </select>
          </div>

          <div className="space-y-2">
            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Interesse / observação
            </label>
            <textarea
              value={form.interesse}
              onChange={(e) => setForm({ ...form, interesse: e.target.value })}
              rows={3}
              placeholder="Ex.: Implante, ortodontia, limpeza..."
              className={`w-full p-3 border rounded-xl text-sm resize-none focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              }`}
            />
          </div>
        </>
      )}
    </BaseModal>
  );
};

export default CreateAppointmentModal;
